import { cn } from "@/lib/utils";
import type { FormulaBreakdown } from "../types";
import { useLanguage } from "../i18n";

export interface FormulaDisplayProps {
  formula: FormulaBreakdown | null;
  className?: string;
}

export function FormulaDisplay({ formula, className }: FormulaDisplayProps) {
  const { t } = useLanguage();

  if (!formula || formula.terms.length === 0) {
    return null;
  }

  // Format a probability value as percentage
  const formatProb = (value: number): string => {
    const percentage = value * 100;
    // Show more precision for small values
    if (percentage < 1) {
      return percentage.toFixed(2);
    }
    return percentage % 1 === 0 ? percentage.toFixed(0) : percentage.toFixed(1);
  };

  // Format total probability
  const totalPercentage = formula.total * 100;
  const formattedTotal = totalPercentage % 1 === 0
    ? totalPercentage.toFixed(0)
    : totalPercentage.toFixed(1);

  return (
    <div className={cn("mt-6 text-center", className)}>
      <h3 className="text-sm font-semibold uppercase tracking-wider text-bb-bone-white/50 mb-2">
        {t.formula}
      </h3>
      <div className="flex flex-wrap items-center justify-center gap-1 text-bb-bone-white/80 font-mono text-sm">
        {formula.terms.map((term, index) => (
          <span key={index} className="flex items-center">
            {index > 0 && <span className="mx-1 text-bb-gold">+</span>}
            <span
              className="text-bb-bone-white"
              title={term.label}
            >
              {formatProb(term.value)}%
            </span>
            <span className="ml-1 text-bb-bone-white/50 text-xs">
              ({term.label})
            </span>
          </span>
        ))}
        <span className="mx-2 text-bb-gold">=</span>
        <span className="text-bb-gold font-bold">{formattedTotal}%</span>
      </div>
    </div>
  );
}
